/**
 * A type that represents a middleware function.
 * @param next A function that calls the next middleware in the chain.
 * @param args The arguments to pass to the middleware function.
 * @returns A promise that resolves when the middleware is done.
 */
export type Middleware<ContextType> = (
    next: () => Promise<any>,
    args: ContextType
) => Promise<any>

/**
 * An interface that represents a middleware chain.
 */
export interface IEndFlowChain<ContextType> {
    use: (funcs: ((args: ContextType) => Promise<any>)[]) => void
    do: (args: ContextType) => Promise<any>
}

/**
 * A class that represents a middleware chain.
 * @example const chain = new EndFlowChain([func1, func2, func3]);
 * @example chain.use([func4, func5]);
 * @example chain.do(data);
 * @example chain.do(data1, data2);
 */
export class EndFlowChain<ContextType> implements IEndFlowChain<ContextType> {
    private middlewares: Middleware<ContextType>[] = []

    constructor(chain: ((args: ContextType) => Promise<any>)[]) {
        this.use(chain)
    }

    /**
     * Adds the functions to the middleware chain.
     * @param funcs The functions to add to the middleware chain.
     */
    public use = (funcs: ((args: ContextType) => Promise<any>)[]): void => {
        for (const func of funcs) {
            const middleware: Middleware<ContextType> = async (next, data) => {
                await func(data)
                await next()
            }
            this.middlewares.push(middleware)
        }
    }

    /**
     * Executes the middleware chain.
     * @param data The data to pass to the middleware chain.
     * @returns A promise that resolves when the middleware chain is done.
     */
    public do = async (args: ContextType): Promise<any> => {
        let index = -1

        const next: () => Promise<any> = async () => {
            index++
            if (index >= this.middlewares.length) {
                return Promise.resolve()
            }

            const middleware = this.middlewares[index]
            return middleware(next, args)
        }

        return next()
    }
}
